import { compiledClientConfig } from "config";
import constate from "constate";
import { useCallback, useEffect, useRef, useState } from "react";
import { io as webSocketIoClient } from "socket.io-client";
import { devLog } from "utils/devtools/devLogging";

const webSocketClient = webSocketIoClient("/", {
  autoConnect: false,
  path: compiledClientConfig.spog ? `/${compiledClientConfig.basePrefix}/ws` : "/ws",
  transports: ["websocket"],
});

const useLiveUpdates = () => {
  const [isConnected, setIsConnected] = useState(false);
  const joinedRooms = useRef(new Set<string>());

  const joinRoom = useCallback(
    (room: string, roomId?: string) => {
      if (webSocketClient.connected) {
        // Only join if not already in the room
        const roomKey = roomId ? `${room}-${roomId}` : room;
        if (!joinedRooms.current.has(roomKey)) {
          devLog({ level: "debug", message: `Joining room: ${roomKey}` });
          webSocketClient.emit(room, roomId);
          joinedRooms.current.add(roomKey);
        }
      }
    },
    [joinedRooms],
  );

  useEffect(() => {
    if (!webSocketClient.connected && compiledClientConfig.enableLiveUpdates) {
      webSocketClient.connect();
      webSocketClient.on("connect", () => {
        devLog({ level: "debug", message: "Connected to live updates server" });
        setIsConnected(true);
      });

      webSocketClient.on("disconnect", () => {
        devLog({ level: "debug", message: "Disconnected from live updates server" });
        setIsConnected(false);
      });
    }
    return () => {
      webSocketClient.off("connect");
      webSocketClient.off("disconnect");
      webSocketClient.disconnect();
      setIsConnected(false);
    };
  }, []);

  return { isConnected, joinRoom, socket: webSocketClient };
};

export const [LiveUpdatesProvider, useLiveUpdatesContext] = constate(useLiveUpdates);
